import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle, ShoppingCart, Repeat } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import axios from 'axios';

export function LeadForm({ calculatorInputs, calculatorResults, selectedChoice, onSubmitted }) {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        bedrijfsnaam: '',
        email: '',
        telefoon: '',
        interesse: selectedChoice || 'lease'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!formData.bedrijfsnaam.trim()) newErrors.bedrijfsnaam = t('form_error_company');
        if (!formData.email.trim()) newErrors.email = t('form_error_email');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('form_error_email_invalid');
        if (!formData.telefoon.trim()) newErrors.telefoon = t('form_error_phone');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                aantal_douches: calculatorInputs.aantalDouches,
                douchebeurten_per_dag: calculatorInputs.douchebeurtenWaarde,
                prijs_per_minuut: calculatorInputs.prijsPerMinuut,
                lease_prijs_per_terminal: calculatorInputs.leasePrijsPerTerminal,
                maanden_open: calculatorInputs.maandenOpen,
                bruto_omzet: calculatorResults.brutoOmzet,
                totale_kosten: calculatorResults.totaleKosten,
                netto_winst_maand: calculatorResults.nettoWinstMaand,
                netto_winst_jaar: calculatorResults.nettoWinstJaar,
                breakeven_prijs_per_minuut: calculatorResults.breakevenPrijsPerMinuut
            };
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads/submit`, payload);
            setIsSubmitted(true);
            if (onSubmitted) onSubmitted();
            toast.success(t('form_toast_success'), { description: t('form_toast_success_desc') });
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error(t('form_toast_error'), { description: t('form_toast_error_desc') });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    if (isSubmitted) {
        return (
            <div className="bg-white/80 rounded-2xl p-8 shadow-lg border border-[#3D3B00]/10 text-center" data-testid="lead-form-success">
                <div className="w-16 h-16 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#4CAF50]" />
                </div>
                <h3 className="text-2xl font-bold text-[#3D3B00] font-['Outfit'] mb-2">{t('form_success_title')}</h3>
                <p className="text-[#3D3B00]/60">
                    {t('form_success_desc').replace('{0}', formData.interesse === 'koop' ? t('form_success_buy') : t('form_success_lease'))}
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white/80 rounded-2xl p-6 lg:p-8 shadow-lg border border-[#3D3B00]/10" data-testid="lead-form">
            <div className="mb-6">
                <h3 className="text-xl lg:text-2xl font-bold text-[#3D3B00] font-['Outfit'] mb-2">{t('form_title')}</h3>
                <p className="text-[#3D3B00]/60">{t('form_subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <Label className="text-[#3D3B00]/80 font-semibold mb-2 block">{t('form_interest')}</Label>
                    <div className="grid grid-cols-2 gap-3">
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, interesse: 'koop' }))}
                            className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                                formData.interesse === 'koop' ? 'border-[#3D3B00] bg-[#3D3B00] text-white' : 'border-[#3D3B00]/20 bg-white text-[#3D3B00] hover:border-[#3D3B00]/40'
                            }`} data-testid="form-interest-koop">
                            <ShoppingCart className="w-5 h-5" />
                            <span className="font-semibold">{t('form_buy')}</span>
                        </button>
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, interesse: 'lease' }))}
                            className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                                formData.interesse === 'lease' ? 'border-[#4CAF50] bg-[#4CAF50] text-white' : 'border-[#3D3B00]/20 bg-white text-[#3D3B00] hover:border-[#4CAF50]'
                            }`} data-testid="form-interest-lease">
                            <Repeat className="w-5 h-5" />
                            <span className="font-semibold">{t('form_lease')}</span>
                        </button>
                    </div>
                </div>

                <div>
                    <Label htmlFor="bedrijfsnaam" className="text-[#3D3B00]/80 font-semibold">{t('form_company')}</Label>
                    <Input id="bedrijfsnaam" data-testid="input-bedrijfsnaam" type="text" placeholder={t('form_company_placeholder')}
                        value={formData.bedrijfsnaam} onChange={handleChange('bedrijfsnaam')}
                        className={`mt-1 h-12 bg-[#F5ECD7]/30 border-[#3D3B00]/15 focus:border-[#4CAF50] focus:ring-[#4CAF50] ${errors.bedrijfsnaam ? 'border-red-500' : ''}`} />
                    {errors.bedrijfsnaam && <p className="text-red-500 text-sm mt-1">{errors.bedrijfsnaam}</p>}
                </div>

                <div>
                    <Label htmlFor="email" className="text-[#3D3B00]/80 font-semibold">{t('form_email')}</Label>
                    <Input id="email" data-testid="input-email" type="email" placeholder={t('form_email_placeholder')}
                        value={formData.email} onChange={handleChange('email')}
                        className={`mt-1 h-12 bg-[#F5ECD7]/30 border-[#3D3B00]/15 focus:border-[#4CAF50] focus:ring-[#4CAF50] ${errors.email ? 'border-red-500' : ''}`} />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                    <Label htmlFor="telefoon" className="text-[#3D3B00]/80 font-semibold">{t('form_phone')}</Label>
                    <Input id="telefoon" data-testid="input-telefoon" type="tel" placeholder={t('form_phone_placeholder')}
                        value={formData.telefoon} onChange={handleChange('telefoon')}
                        className={`mt-1 h-12 bg-[#F5ECD7]/30 border-[#3D3B00]/15 focus:border-[#4CAF50] focus:ring-[#4CAF50] ${errors.telefoon ? 'border-red-500' : ''}`} />
                    {errors.telefoon && <p className="text-red-500 text-sm mt-1">{errors.telefoon}</p>}
                </div>

                <Button type="submit" data-testid="submit-lead-btn" disabled={isSubmitting}
                    className="w-full h-14 bg-[#3D3B00] hover:bg-[#2D2B00] text-[#F5ECD7] font-bold text-lg rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all active:scale-95">
                    {isSubmitting ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('form_sending')}</>
                    ) : (
                        <><Send className="w-5 h-5 mr-2" />{t('form_submit')}</>
                    )}
                </Button>
            </form>

            <p className="text-xs text-[#3D3B00]/40 text-center mt-4">{t('form_privacy')}</p>
        </div>
    );
}
